import React, { useEffect, useRef, useState } from "react";
import { useCreateNewJobDescription } from "../../../store/query/pythonQuery";
import { useFetchCitiesByCountry } from "../../../store/query/globalTalentSearchQuery";
import { ExternalApiEndPoint } from "../../../api-routes";
import copy from "copy-to-clipboard";

import logo from "../../../assets/Images/element-wordmark-dark.svg";
import colors from "../../../theme/colors";

const LOGO_VIEWBOX_WIDTH = 200;
const LOGO_VIEWBOX_HEIGHT = 48;
const LOGO_ASPECT_RATIO = LOGO_VIEWBOX_WIDTH / LOGO_VIEWBOX_HEIGHT;

const loadImageAsPngDataUrl = (src, renderHeight = 96) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const aspect =
        img.naturalWidth && img.naturalHeight
          ? img.naturalWidth / img.naturalHeight
          : LOGO_ASPECT_RATIO;
      const canvasHeight = renderHeight;
      const canvasWidth = Math.round(canvasHeight * aspect);
      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = src;
  });

const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "");
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const PDF_THEME = {
  primary: hexToRgb(colors.primary),
  link: hexToRgb(colors.link),
  body: [51, 65, 85],
  muted: [99, 115, 129],
};

const countryOptions = [
  {
    name: "United States",
    code: "US",
    flag: `${ExternalApiEndPoint.FLAG_CDN}/us.png`,
  },
  {
    name: "India",
    code: "IN",
    flag: `${ExternalApiEndPoint.FLAG_CDN}/in.png`,
  },
];

const formatLocationsForSubmit = (locations) => {
  const groups = {};
  const order = [];
  for (const loc of locations) {
    const dashIdx = loc.indexOf(" - ");
    if (dashIdx === -1) {
      order.push(loc);
      continue;
    }
    const code = loc.substring(0, dashIdx);
    const city = loc.substring(dashIdx + 3);
    if (!groups[code]) {
      groups[code] = [];
      order.push(code);
    }
    groups[code].push(city);
  }
  return order
    .map((key) => (groups[key] ? `${key} - ${groups[key].join(", ")}` : key))
    .join(", ");
};

const useCreateNewJD = () => {
  const [newJDDescriptions, setNewJDDescriptions] = useState("");
  const [alertDetail, setAlertDetail] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [jdDetails, setJdDetails] = useState(null);
  const [copyJd, setCopyJd] = useState({});
  const [jdLevel, setjdLevel] = useState(null);
  const [organisationName, setOrganisationName] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationInputs, setLocationInputs] = useState([]);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState("");

  const [loadingCities, setLoadingCities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollResultRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState(false);

  const createNewJobDescriptionMutation = useCreateNewJobDescription();
  const fetchCitiesMutation = useFetchCitiesByCountry();

  const level = [
    { value: "Junior", label: "Junior" },
    { value: "Middle", label: "Middle" },
    { value: "Senior", label: "Senior" },
    { value: "Leadership", label: "Leadership" },
  ];
  const mapLevelToApi = (value) => {
    if (!value) return "";
    if (value === "Leadership") return "leadership";
    return String(value).toLowerCase();
  };

  const handleCreateNewJD = () => {
    setIsSubmitting(true);
    createNewJobDescriptionMutation.mutate(
      {
        prompt: newJDDescriptions,
        level: mapLevelToApi(jdLevel?.value),
        location: formatLocationsForSubmit(locations),
        organisation_name: organisationName.trim(),
      },
      {
        onSuccess: (data) => {
          setIsSubmitting(false);
          setJdDetails(data);
          setCopyJd(data);
        },
        onError: () => {
          setIsSubmitting(false);
        },
      },
    );
  };
  const handleSetNewJDDescription = (event) => {
    const { value } = event.target;
    setNewJDDescriptions(value);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setLoadingCities(true);
    setCitySearchQuery("");
    setCities([]);
    fetchCitiesMutation.mutate(country.name, {
      onSuccess: (result) => {
        if (result && !result.error && result.data) {
          setCities(result.data);
        }
        setLoadingCities(false);
      },
      onError: () => {
        setCities([]);
        setLoadingCities(false);
      },
    });
  };

  const handleCitySelect = (city) => {
    const code = selectedCountry?.code || "";
    const locationDisplay = `${code} - ${city}`;
    if (locations.includes(locationDisplay)) {
      setLocationInputs((prev) => prev.filter((l) => l !== locationDisplay));
      setLocations((prev) => prev.filter((l) => l !== locationDisplay));
    } else {
      setLocationInputs((prev) => [...prev, locationDisplay]);
      setLocations((prev) => [...prev, locationDisplay]);
    }
  };

  const handleLocationRemove = (locationToRemove) => {
    setLocationInputs((prev) => prev.filter((l) => l !== locationToRemove));
    setLocations((prev) => prev.filter((l) => l !== locationToRemove));
  };

  const handleLocationDropdownClose = () => {
    setLocationDropdownOpen(false);
    setSelectedCountry(null);
    setCities([]);
    setCitySearchQuery("");
  };

  const handleLocationClear = () => {
    setLocationInputs([]);
    setLocations([]);
    setLocationDropdownOpen(false);
    setSelectedCountry(null);
    setCities([]);
    setCitySearchQuery("");
  };

  const filteredCities = cities.filter((city) =>
    city.toLowerCase().includes(citySearchQuery.toLowerCase()),
  );

  const handleSetOrganisationName = (event) => {
    const { value } = event.target;
    setOrganisationName(value);
  };

  const handleCopyJD = () => {
    if (copyJd) {
      setCopyStatus(true);
      const plainText = formatPlainText(copyJd);
      copy(plainText);
    }
    setTimeout(() => {
      setCopyStatus(false);
    }, 3000);
  };

  const formatPlainText = (data) => {
    const req = data.requirements || {};
    const mandatory =
      req.mandatory_skills?.length || req.optional_skills?.length
        ? req.mandatory_skills || []
        : req.technical_skills || [];
    const optional = req.optional_skills || [];
    const soft = req.soft_skills || [];

    return `
Job Title: ${data.job_title ?? ""}
Company: ${data.company ?? ""}
Location: ${data.location ?? ""}
Job Type: ${data.job_type ?? ""}
Experience Level: ${data.experience_level ?? ""}
Years of Experience: ${data.years_of_experience ?? ""}

Job Description:
${data.job_description ?? ""}

Key Responsibilities:
${(data.responsibilities || []).map((item) => `- ${item}`).join("\n")}

Requirements:
Education: ${req.education ?? ""}
Experience: ${req.experience ?? ""}

Mandatory Skills:
${mandatory.map((item) => `- ${item}`).join("\n")}
Optional Skills:
${optional.map((item) => `- ${item}`).join("\n")}

Soft Skills:
${soft.map((item) => `- ${item}`).join("\n")}

Preferred Qualifications:
${(data.preferred_qualifications || []).map((item) => `- ${item}`).join("\n")}

Why Join Us:
${data.why_join_us ?? ""}
  `;
  };

  useEffect(() => {
    if (jdDetails && scrollResultRef.current) {
      scrollResultRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [jdDetails]);

  const handleExport = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let y = margin;

    const job = jdDetails;

    const checkPageBreak = (height = 10) => {
      if (y + height > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoHeightMm = 12;
    const logoWidthMm = logoHeightMm * LOGO_ASPECT_RATIO;
    const logoX = pageWidth - margin - logoWidthMm;
    const logoY = 12;

    const logoDataUrl = await loadImageAsPngDataUrl(logo, 144);
    doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidthMm, logoHeightMm);

    y = logoY + logoHeightMm + 6;
    doc.setDrawColor(...PDF_THEME.primary);
    doc.setLineWidth(1.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...PDF_THEME.primary);
    doc.text(job?.job_title, margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...PDF_THEME.body);
    doc.text(`${job.company}`, margin, y);
    y += 10;

    doc.setFontSize(10);
    doc.setTextColor(...PDF_THEME.muted);
    const metaParts = [
      job.job_type,
      job.experience_level,
      job.years_of_experience,
      job.location,
    ].filter(Boolean);
    doc.text(metaParts.join(" | "), margin, y);
    y += 15;

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_THEME.link);
    doc.text("Job Description", margin, y);
    doc.setTextColor(...PDF_THEME.body);
    y += 8;

    const writeWrappedText = (prefix, text, indent = 0) => {
      if (!text) return;
      const availableWidth = 165 - indent;
      const fullString = prefix ? `${prefix} ${text}` : String(text);
      const lines = doc.splitTextToSize(fullString, availableWidth);
      lines.forEach((line) => {
        checkPageBreak(6);
        doc.text(line, margin + indent, y);
        y += 6;
      });
    };

    doc.setFont("helvetica", "normal");
    const description = doc.splitTextToSize(job.job_description, 165);
    description.forEach((line) => {
      checkPageBreak();
      doc.text(line, margin, y);
      y += 6;
    });

    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_THEME.link);
    doc.text("Responsibilities", margin, y);
    doc.setTextColor(...PDF_THEME.body);
    y += 8;

    doc.setFont("helvetica", "normal");
    (job.responsibilities || []).forEach((item) => {
      writeWrappedText("•", item, 0);
    });

    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_THEME.link);
    doc.text("Requirements", margin, y);
    doc.setTextColor(...PDF_THEME.body);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.text("Education", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    if (job.requirements?.education) {
      writeWrappedText("", job.requirements.education, 5);
      y += 2;
    }

    doc.setFont("helvetica", "bold");
    doc.text("Experience", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    if (job.requirements?.experience) {
      writeWrappedText("", job.requirements.experience, 5);
      y += 2;
    }

    const req = job.requirements || {};
    const mandatorySkills =
      req.mandatory_skills?.length || req.optional_skills?.length
        ? req.mandatory_skills || []
        : req.technical_skills || [];
    const optionalSkills = req.optional_skills || [];

    doc.setFont("helvetica", "bold");
    doc.text("Mandatory Skills", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    mandatorySkills.forEach((skill) => {
      writeWrappedText("-", skill, 5);
    });

    y += 4;
    doc.setFont("helvetica", "bold");
    doc.text("Optional Skills", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    optionalSkills.forEach((skill) => {
      writeWrappedText("-", skill, 5);
    });

    y += 4;
    doc.setFont("helvetica", "bold");
    doc.text("Soft Skills", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    (req.soft_skills || []).forEach((skill) => {
      writeWrappedText("-", skill, 5);
    });

    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_THEME.link);
    doc.text("Preferred Qualifications", margin, y);
    doc.setTextColor(...PDF_THEME.body);
    y += 6;

    doc.setFont("helvetica", "normal");
    (job.preferred_qualifications || []).forEach((q) => {
      writeWrappedText("•", q, 0);
    });

    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_THEME.link);
    doc.text("Why Join Us?", margin, y);
    doc.setTextColor(...PDF_THEME.body);
    y += 6;

    doc.setFont("helvetica", "normal");
    const whyText = doc.splitTextToSize(job.why_join_us || "", 165);
    whyText.forEach((line) => {
      checkPageBreak();
      doc.text(line, margin, y);
      y += 6;
    });

    const safePdfTitle = (job.job_title || "Job_Description")
      .replace(/[/\\?%*:|"<>]/g, "")
      .replace(/\s+/g, "_");
    doc.save(`${safePdfTitle}_Job_Description.pdf`);
  };

  const handleExportDoc = async () => {
    const job = jdDetails;
    if (!job) return;

    // eslint-disable-next-line global-require
    const { Document, Packer, Paragraph, TextRun, HeadingLevel } =
      require("docx");

    const req = job.requirements || {};
    const mandatorySkills =
      req.mandatory_skills?.length || req.optional_skills?.length
        ? req.mandatory_skills || []
        : req.technical_skills || [];
    const optionalSkills = req.optional_skills || [];
    const softSkills = req.soft_skills || [];

    const children = [];

    const pushLines = (text) => {
      if (!text) return;
      const lines = String(text).split(/\n+/);
      lines.forEach((line) => {
        const t = line.trim();
        if (!t) return;
        children.push(new Paragraph({ children: [new TextRun(t)] }));
      });
    };

    children.push(
      new Paragraph({
        text: job.job_title || "Job Description",
        heading: HeadingLevel.HEADING_1,
      }),
    );

    if (job.company) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: job.company, bold: true })],
        }),
      );
    }

    const metaLine = [
      job.job_type,
      job.experience_level,
      job.years_of_experience,
      job.location,
    ]
      .filter(Boolean)
      .join(" | ");
    if (metaLine) {
      children.push(new Paragraph({ text: metaLine }));
    }

    children.push(new Paragraph({ text: "" }));

    children.push(
      new Paragraph({
        text: "Job Description",
        heading: HeadingLevel.HEADING_2,
      }),
    );
    pushLines(job.job_description || "");

    children.push(
      new Paragraph({
        text: "Key Responsibilities",
        heading: HeadingLevel.HEADING_2,
      }),
    );
    (job.responsibilities || []).forEach((item) => {
      children.push(new Paragraph({ text: item, bullet: { level: 0 } }));
    });

    children.push(
      new Paragraph({
        text: "Requirements",
        heading: HeadingLevel.HEADING_2,
      }),
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: "Education: ", bold: true }),
          new TextRun(req.education || ""),
        ],
      }),
    );
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: "Experience: ", bold: true }),
          new TextRun(req.experience || ""),
        ],
      }),
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: "Mandatory skills", bold: true })],
      }),
    );
    mandatorySkills.forEach((skill) => {
      children.push(new Paragraph({ text: skill, bullet: { level: 0 } }));
    });

    children.push(
      new Paragraph({
        children: [new TextRun({ text: "Optional skills", bold: true })],
      }),
    );
    optionalSkills.forEach((skill) => {
      children.push(new Paragraph({ text: skill, bullet: { level: 0 } }));
    });

    children.push(
      new Paragraph({
        children: [new TextRun({ text: "Soft skills", bold: true })],
      }),
    );
    softSkills.forEach((skill) => {
      children.push(new Paragraph({ text: skill, bullet: { level: 0 } }));
    });

    children.push(
      new Paragraph({
        text: "Preferred Qualifications",
        heading: HeadingLevel.HEADING_2,
      }),
    );
    (job.preferred_qualifications || []).forEach((q) => {
      children.push(new Paragraph({ text: q, bullet: { level: 0 } }));
    });

    children.push(
      new Paragraph({
        text: "Why Join Us?",
        heading: HeadingLevel.HEADING_2,
      }),
    );
    pushLines(job.why_join_us || "");

    const doc = new Document({
      sections: [
        {
          children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const safeTitle = (job.job_title || "Job_Description")
      .replace(/[/\\?%*:|"<>]/g, "")
      .replace(/\s+/g, "_");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeTitle}_Job_Description.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return {
    newJDDescriptions,
    jdDetails,
    jdLevel,
    level,
    organisationName,
    copyStatus,
    locations,
    locationInputs,
    locationDropdownOpen,
    setLocationDropdownOpen,
    selectedCountry,
    countryOptions,
    citySearchQuery,
    setCitySearchQuery,
    loadingCities,
    filteredCities,
    handleCountrySelect,
    handleCitySelect,
    handleLocationDropdownClose,
    handleLocationRemove,
    handleLocationClear,
    alertDetail,
    isSubmitting,
    copyJd,
    scrollResultRef,

    handleSetOrganisationName,
    setAlertDetail,
    setIsSubmitting,
    setjdLevel,
    handleSetNewJDDescription,
    handleCreateNewJD,
    setCopyStatus,
    handleCopyJD,
    handleExport,
    handleExportDoc,
  };
};

export default useCreateNewJD;
